import axios, { AxiosRequestConfig } from "axios";
import {
  AggregationFacet,
  SearchAggregationResult,
  SearchResult,
  SortFacet,
} from "../../from-server/asin-central/search";
import {
  BuyBoxRequest,
  FullAsinWithAudits,
  Requester,
  RestrictedAsin,
} from "../../from-server/asin-central/model";
import { AuthorizedAttribute } from "../../from-server/brass/model";
import {
  GetProductLinesForUserResponse,
  GetProgramsForUserResponse,
  ProductLine,
  Program,
  ProtectedProgram,
} from "@amzn/coral_com-amazon-programproductlineservice";
import {
  AsinMarketplaceLaunchAttributesList,
  AsinMarketplaceList,
  AsinMarketplaces,
  AsinMarketplaceStampList,
  AsinMarketplaceValidationDetailPassException,
  AsinMarketplaceWeblabList,
  BaselineRequest,
  BatchDeleteAsinMarketplacesWeblabInput,
  CorrectCatalogDefaults,
  CreateAsinMarketplaceValidationDetailPassExceptionsInput,
  GetBaselineRequestInput,
  GetCandidateDatesOutput,
  GetCorrectCatalogDefaultsOutput,
  GetLaunchWorkflowsByRequesterInput,
  GetLaunchWorkflowsByRequesterOutput,
  LaunchControlDate,
  LaunchWorkflowInfo,
  PublishCorrectCatalogDefaultsInput,
  PublishCorrectCatalogDefaultsOutput,
  PublishStampOutput,
  PublishWeblabOutput,
  ValidateAsinMarketplacesOutput,
} from "@amzn/coral_com-amazon-deviceslaunchcontrolservice";
import logger from "../../art-logger-config";
import { defaultSort } from "../porAsinList/TableCommonElements";
import {
  AsinMarketplaceDates,
  DateTypes,
  AsinMarketplaceDate,
} from "@amzn/coral_com-amazon-deviceslaunchcontrolservice";
import { Schedule } from "../../from-server/milestone/model";
import { AsinMarketplaceValidationResult } from "@amzn/coral_com-amazon-asincentral-common";
import { ValidationAttributeDetailStep } from "../validation/validationAttribute";
import { getBackendHost } from "../../constants/common";
const maxPageSize = 500;
const maxRows = 10000;

axios.interceptors.request.use(
  async (config: AxiosRequestConfig) => {
    if (!config.baseURL) {
      config.baseURL = await getBackendHost();
    }
    const token = window.loom.getAccessToken();
    if (token) {
      config.headers!["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error: any) => {
    return Promise.reject(error);
  }
);

function serializeSortFacets(sortFacets: SortFacet[]): string {
  return sortFacets.map((f) => `${f.attributeName}:${f.ascending}`).join(",");
}

function serializeAggregationFacets(
  aggregationFacets: AggregationFacet[]
): string {
  return aggregationFacets
    .map(
      (f) =>
        `${f.attributeName}:${f.aggregationType}:${f.totalAggregationCount}`
    )
    .join(",");
}

export async function searchAsins(
  offset = 0,
  pageSize = 30,
  query = "",
  sortFacets: SortFacet[] = defaultSort
): Promise<SearchResult> {
  const { data } = await axios.get<SearchResult>("/search", {
    params: {
      query: query,
      size: Math.min(pageSize, maxRows - offset),
      from: offset,
      sort: serializeSortFacets(sortFacets),
    },
    paramsSerializer: (params) => {
      return Object.keys(params)
        .map((key) => `${key}=${encodeURIComponent(params[key])}`)
        .join("&");
    },
  });
  return data;
}

export async function searchAggregations(
  query = "",
  aggregationFacets: AggregationFacet[]
): Promise<SearchAggregationResult> {
  const { data } = await axios.get<SearchAggregationResult>("/search-agg", {
    params: {
      query: query,
      aggregation: serializeAggregationFacets(aggregationFacets),
    },
    paramsSerializer: (params) => {
      return Object.keys(params)
        .map((key) => `${key}=${encodeURIComponent(params[key])}`)
        .join("&");
    },
  });
  return data;
}

export const searchPorAsinsNoLimit = async (query: string) => {
  let allAsins: (FullAsinWithAudits | RestrictedAsin)[] = [];
  let offset = 0;
  let page;
  let totalAsins = 10000000;
  while (offset < totalAsins) {
    page = await searchAsins(offset, maxPageSize, query, defaultSort);
    totalAsins = page.totalResultCount || 0;
    allAsins = allAsins.concat(page.asins);
    offset = page.asins.length + offset;
  }
  return allAsins;
};

export async function getUser(): Promise<Requester> {
  const { data } = await axios.get<Requester>("/get-user");
  return data;
}

export async function setLaunchAttributes(
  asins: FullAsinWithAudits[]
): Promise<void> {
  const url = `/launch-attributes`;
  const payload = asins.map((it) => {
    return {
      asin: it.asin,
      marketplace: it.marketplace,
      userDescription: it.userDescription,
    };
  });
  const chunkSize = 25;
  for (let i = 0; i < payload.length; i += chunkSize) {
    const chunk = payload.slice(i, i + chunkSize);
    await axios.put(url, chunk);
  }
}

export type ValidateAsinMarketplacesResult = {
  validateAsinMarketplacesOutput: ValidateAsinMarketplacesOutput;
  status: number;
  statusText: string;
};

export async function validateAsinMarketplaces(
  asins: FullAsinWithAudits[],
  validationServiceId: string,
  reportResultOnly: boolean,
  queryString?: string
): Promise<ValidateAsinMarketplacesResult> {
  const url = `/validate-asin-marketplaces`;
  const asinPayload = asins.map((it) => {
    return {
      asin: it.asin,
      marketplace: it.marketplace,
    };
  });
  const payload: any = {
    asinMarketplaces: asinPayload,
    validation: validationServiceId,
    reportResultOnly: reportResultOnly,
  };
  if (queryString !== undefined) {
    payload.queryString = queryString;
  }
  const { data, status, statusText } =
    await axios.put<ValidateAsinMarketplacesOutput>(url, payload);

  return {
    validateAsinMarketplacesOutput: data,
    status: status,
    statusText: statusText,
  };
}

export interface BuyBoxResult {
  buyBoxEntries: BuyBoxResultEntry[];
}
export interface BuyBoxResultEntry {
  asin: string;
  obfuscatedMarketplaceId: string;
  buyBoxStatus: string;
}

export async function fetchBuyBoxStatus(
  asins: FullAsinWithAudits[]
): Promise<BuyBoxResult> {
  const url = `/fetch-buy-box-status`;
  const payload: BuyBoxRequest = {
    records: asins.map((it) => ({
      asin: it.asin,
      obfuscatedMarketplaceId: it.obfuscatedMarketplaceId,
      buyBoxStatus: it.buyBoxStatus,
    })),
  };

  const { data } = await axios.post<BuyBoxResult>(url, payload);
  return data;
}

function getOverallResult(
  asins: FullAsinWithAudits[],
  asinId: string,
  serviceId: string
) {
  // Get the asin that was modified. There has to be a better way to do this...
  const asin = asins.find((asin) => asin.asin === asinId);
  if (!asin) {
    return "";
  }

  //TODO: This is ripe for refactoring, persisting the value that changed would be nice.
  switch (serviceId) {
    case "ManualDGL":
      return asin.validationOverallResultManualDGL;
    case "ManualRL":
      return asin.validationOverallResultManualRL;
    case "ManualInstock":
      return asin.validationOverallResultManualInstock;
    case "ManualOX":
      return asin.validationOverallResultManualOX;
    case "ManualGTM":
      return asin.validationOverallResultManualGTM;
    case "ManualCS":
      return asin.validationOverallResultManualCS;
    default:
      return "";
  }
}

export async function reportValidationResult(
  asinMarketplaceList: AsinMarketplaceList,
  asins: FullAsinWithAudits[],
  validationId: string,
  validationStatus: string,
  validationServiceId: string
): Promise<void> {
  const url = `/report-validation-result`;
  const asinPayload = asinMarketplaceList.map((it) => {
    return {
      asin: it.asin,
      obfuscatedMarketplaceId: it.obfuscatedMarketplaceId,
      detail: [],
      overallResult: getOverallResult(asins, it.asin, validationServiceId),
    };
  });
  const payload = {
    validationId: validationId,
    validationResults: asinPayload,
    validationStatus: validationStatus,
  };

  await axios.put<ValidateAsinMarketplacesOutput>(url, payload);
}

export async function getCandidateDates(
  asinMarketplaces: AsinMarketplaces
): Promise<GetCandidateDatesOutput> {
  const url = `/get-candidate-dates`;
  const payload = {
    asinMarketplaces: asinMarketplaces,
  };

  const { data } = await axios.put<GetCandidateDatesOutput>(url, payload);
  return data;
}

export async function getLaunchWorkflowsByRequester(
  input: GetLaunchWorkflowsByRequesterInput
): Promise<GetLaunchWorkflowsByRequesterOutput> {
  const url = `/get-launch-workflows`;
  const { data } = await axios.put<GetLaunchWorkflowsByRequesterOutput>(
    url,
    input
  );
  return data;
}

export async function recordAsinMarketplaceDates(
  asinMarketplaceDates: AsinMarketplaceDates,
  dateTypes: DateTypes,
  scheduleId?: string,
  programId?: string,
  baselineRequest?: BaselineRequest
): Promise<BaselineRequest> {
  const url = `/record-asin-marketplace-dates`;
  const payload = {
    baselineRequest: baselineRequest,
    asinMarketplaceDates: asinMarketplaceDates,
    dateTypes: dateTypes,
    programId: programId,
    scheduleId: scheduleId,
  };

  const { data } = await axios.put<BaselineRequest>(url, payload);
  return data;
}

export async function getBaselineRequest(
  baselineId: string
): Promise<BaselineRequest> {
  const url = "/get-baseline-request";
  const payload = {
    baselineId: baselineId,
  };
  const { data } = await axios.put<BaselineRequest>(url, payload);
  return data;
}

export async function getSchedulesByprogram(
  programId: string
): Promise<Schedule[]> {
  const url = "/get-schedules-by-program";
  const payload = {
    programId: programId,
  };
  const { data } = await axios.put<Schedule[]>(url, payload);
  return data;
}

export async function publishLaunchAttributes(
  asinMarketplaceLaunchData: AsinMarketplaceLaunchAttributesList
): Promise<LaunchWorkflowInfo> {
  const url = `/publish-launch-attributes`;
  const payload = {
    asinMarketplaceLaunchData: asinMarketplaceLaunchData,
  };

  const { data } = await axios.put<LaunchWorkflowInfo>(url, payload);
  return data;
}

export async function publishStamps(
  asinMarketplaceStamps: AsinMarketplaceStampList
): Promise<PublishStampOutput> {
  const url = `/publish-stamps`;
  const payload = {
    asinMarketplaceStamps: asinMarketplaceStamps,
  };

  const { data } = await axios.put<PublishStampOutput>(url, payload);
  return data;
}

export async function publishWeblabs(
  asinMarketplaceWeblabs: AsinMarketplaceWeblabList
): Promise<PublishWeblabOutput> {
  const url = `/publish-weblabs`;
  const payload = {
    asinMarketplaceWeblabs: asinMarketplaceWeblabs,
  };

  const { data } = await axios.put<PublishWeblabOutput>(url, payload);
  return data;
}

export async function getCorrectCatalogDefaults(
  announceDate: Date | undefined,
  streetDate: Date | undefined,
  country: string,
  productLineId: string,
  party: string,
  asinType: string,
  itemType?: string
): Promise<GetCorrectCatalogDefaultsOutput> {
  const url = `/get-correct-catalog-defaults`;
  const announceDateNumber = announceDate
    ? new Date(announceDate).getTime()
    : undefined;
  const streetDateNumber = streetDate
    ? new Date(streetDate).getTime()
    : undefined;
  const payload = {
    announceDate: announceDateNumber,
    streetDate: streetDateNumber,
    country: country,
    productLineId: productLineId,
    party: party,
    asinType: asinType,
    itemType: itemType ? itemType : "UNK",
  };

  const { data } = await axios.put<GetCorrectCatalogDefaultsOutput>(
    url,
    payload
  );
  return data;
}

export async function removeWeblabFromAsinMarketplaces(
  asinMarketplaces: AsinMarketplaceList
): Promise<LaunchWorkflowInfo> {
  const url = `/remove-weblab-from-asin-marketplaces`;
  const payload = {
    asinMarketplaces: asinMarketplaces,
  };

  const { data } = await axios.put<LaunchWorkflowInfo>(url, payload);
  return data;
}

function hasAnnounceDateChanged(
  oldAsin: FullAsinWithAudits,
  newAsin: FullAsinWithAudits
): boolean {
  return oldAsin.announceDate != newAsin.announceDate;
}

function hasOnlineStreetDateChanged(
  oldAsin: FullAsinWithAudits,
  newAsin: FullAsinWithAudits
): boolean {
  return oldAsin.onlineStreetDate != newAsin.onlineStreetDate;
}

function hasUserDescriptionChanged(
  oldAsin: FullAsinWithAudits,
  newAsin: FullAsinWithAudits
): boolean {
  return oldAsin.userDescription != newAsin.userDescription;
}

function convertDatesToPSTInMillis(date: Date): number {
  return Date.UTC(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    // We store all dates in PST with an 8 hour offset
    // See: https://t.corp.amazon.com/V1205902706/communication
    // See: https://code.amazon.com/packages/DevicesLaunchControlService/commits/1cb292833b12c9ad2d232eedc2a1bf1f215c2c60#
    8
  );
}

function getLaunchControlDate(
  dateType: string,
  date: Date | undefined
): LaunchControlDate {
  return LaunchControlDate.clone({
    dateType: dateType,
    // TODO: we might need to handle null/unassigned dates using epoch millis in the future
    // See: https://issues.amazon.com/issues/LEAP-11102
    dateValue: date ? convertDatesToPSTInMillis(date) : undefined,
  });
}

function getLaunchControlDates(
  oldAsin: FullAsinWithAudits,
  newAsin: FullAsinWithAudits
): LaunchControlDate[] {
  const dates: LaunchControlDate[] = [];
  if (hasAnnounceDateChanged(oldAsin, newAsin)) {
    dates.push(getLaunchControlDate("announceDate", newAsin.announceDate));
  }
  if (hasOnlineStreetDateChanged(oldAsin, newAsin)) {
    dates.push(
      getLaunchControlDate("onlineStreetDate", newAsin.onlineStreetDate)
    );
  }
  return dates;
}

export function prepareRecordAsinMarketplaceDatesWithAsins(
  currentPagePorAsins: FullAsinWithAudits[],
  updatedEntries: FullAsinWithAudits[],
  editedRowsSet: { [index: number]: boolean }
) {
  const asinMarketplaceDates: AsinMarketplaceDate[] = [];
  const dateTypes: Set<string> = new Set<string>();

  Object.keys(editedRowsSet).forEach((idx) => {
    const oldAsin = currentPagePorAsins[+idx];
    const newAsin = updatedEntries[+idx];

    if (
      hasAnnounceDateChanged(oldAsin, newAsin) ||
      hasOnlineStreetDateChanged(oldAsin, newAsin)
    ) {
      asinMarketplaceDates.push(
        AsinMarketplaceDate.clone({
          asin: newAsin.asin,
          obfuscatedMarketplaceId: newAsin.obfuscatedMarketplaceId,
          dates: getLaunchControlDates(oldAsin, newAsin),
        })
      );
    }

    if (hasAnnounceDateChanged(oldAsin, newAsin)) {
      dateTypes.add("announceDate");
    }
    if (hasOnlineStreetDateChanged(oldAsin, newAsin)) {
      dateTypes.add("onlineStreetDate");
    }
  });
  return {
    asinMarketplaceDates: asinMarketplaceDates,
    dateTypes: dateTypes,
  };
}

export async function recordAsinMarketplaceDatesWithAsins(
  asinMarketplaceDates: AsinMarketplaceDate[],
  dateTypes: Set<string>
) {
  return await recordAsinMarketplaceDates(
    asinMarketplaceDates,
    Array.from(dateTypes)
  );
}

export function prepareSetLaunchAttributesWithAsins(
  currentPagePorAsins: FullAsinWithAudits[],
  updatedEntries: FullAsinWithAudits[],
  editedRowsSet: { [index: number]: boolean }
) {
  const asinMarketplaceAttributes: FullAsinWithAudits[] = [];

  Object.keys(editedRowsSet).forEach((idx) => {
    const oldAsin = currentPagePorAsins[+idx];
    const newAsin = updatedEntries[+idx];

    if (hasUserDescriptionChanged(oldAsin, newAsin)) {
      asinMarketplaceAttributes.push(newAsin);
    }
  });
  return asinMarketplaceAttributes;
}

export async function setLaunchAttributesWithAsins(
  asinMarketplaceAttributes: FullAsinWithAudits[]
) {
  return await setLaunchAttributes(asinMarketplaceAttributes);
}

export async function getEditableAttributes(): Promise<AuthorizedAttribute[]> {
  const { data } = await axios.get<AuthorizedAttribute[]>(
    "/user-editable-attributes"
  );
  return data;
}

export async function getMakoWeblabString(
  asin: string,
  obfuscatedMarketplaceId: string
): Promise<any> {
  const url = `/get-mako-weblab-string`;
  const payload = {
    asin: asin,
    obfuscatedMarketplaceId: obfuscatedMarketplaceId,
  };

  const { data } = await axios.post<any>(url, payload);
  return data;
}

export type ProgramIdAndName = {
  name?: string;
  id?: string;
};

export async function getLiveness(
  asin: string,
  marketplaceId: number
): Promise<any> {
  const url = `/get-liveness`;
  const { data } = await axios.get<any>(url, {
    params: {
      asin: asin,
      marketplaceId: marketplaceId,
    },
  });
  return data;
}

export async function getProgramsNamesForUser(): Promise<ProgramIdAndName[]> {
  const url = `/programs`;
  const { data } = await axios.get<GetProgramsForUserResponse>(url);
  const getProgramNameAndId = (
    program: Program | ProtectedProgram
  ): ProgramIdAndName => ({
    name: program.name,
    id: program.conceptId,
  });
  const nameExists = (value: ProgramIdAndName): boolean =>
    value.name !== undefined;
  return [
    ...(data.programs ?? []).map(getProgramNameAndId).filter(nameExists),
    ...(data.protectedPrograms ?? [])
      .map(getProgramNameAndId)
      .filter(nameExists),
  ];
}

export async function getProductLinesForUser(): Promise<ProductLine[]> {
  const url = `/productLines`;
  const { data } = await axios.get<GetProductLinesForUserResponse>(url);
  const nameExists = (value: ProductLine): boolean => value.name !== undefined;
  return [...(data.productLines ?? []).filter(nameExists)];
}

export async function getAmazonTinyUrl(url: string): Promise<string> {
  try {
    const externalAxios = axios.create({
      timeout: 5000,
      headers: {
        "Content-Type": "application/json",
      },
    });

    const tinyUrl = `https://tiny.amazon.com/submit/url?name=${encodeURIComponent(
      url
    )}`;
    const response = await externalAxios.get(tinyUrl);
    logger.info(
      `Tiny url generated with response: ${JSON.stringify(response)}`
    );
    if (response && response.data) {
      return response.data.short_url;
    }
  } catch (error: any) {
    logger.error(`Error generating tiny url: ${error.toString()}`);
  }
  return url;
}

function getRelevantValidationId(
  asin: FullAsinWithAudits | null,
  service: string
): string {
  if (!asin) return "";
  switch (service) {
    case "LVS":
      return asin?.validationRequestLVS;
    case "Hattrick":
      return asin?.validationRequestHattrick;
    default:
      return "";
  }
}

export async function createAsinMarketplaceValidationDetailPassExceptions(
  asin: FullAsinWithAudits | null,
  detailSteps: ValidationAttributeDetailStep[],
  service: string
): Promise<AsinMarketplaceValidationResult> {
  const url = `/create-asin-marketplace-validation-detail-pass-exceptions`;
  const validationId = getRelevantValidationId(asin, service);
  const payload = {
    asin: asin?.asin,
    obfuscatedMarketplaceId: asin?.obfuscatedMarketplaceId,
    detailPassExceptions:
      detailSteps
        .filter((step) =>
          ["pass_exception", "pass_exception_one_time", "fail"].includes(
            step.result
          )
        )
        .map((step) =>
          AsinMarketplaceValidationDetailPassException.clone({
            step: step.step,
            passExceptionReason:
              step.result == "fail"
                ? ""
                : step.passExceptionReason || "unknown reason",
            temporary: step.result === "pass_exception_one_time",
          })
        ) || [],
    validationId: validationId,
  };

  const { data } = await axios.put<AsinMarketplaceValidationResult>(
    url,
    payload
  );
  return data;
}

export async function publishCorrectCatalogDefaults(
  asin: FullAsinWithAudits | null,
  brand: string | undefined,
  brandCode: string | undefined,
  deprecatedCanBeGiftmessaged: boolean | undefined,
  deprecatedCanBeGiftwrapped: boolean | undefined,
  displayAttribute: string | undefined,
  displayOnWebsite: string | undefined,
  glProductTypeGroupType: string | undefined,
  itemTypeKeyword: string | undefined,
  manufacturer: string | undefined,
  productCategory: string | undefined,
  productSubCategory: string | undefined,
  productType: string | undefined,
  recommendedBrowseNodes: string | undefined,
  sellerOfRecordVendorId: string | undefined,
  title: string | undefined,
  bulletPoint: string | undefined
): Promise<PublishCorrectCatalogDefaultsOutput> {
  const url = `/publish-correct-catalog-defaults`;
  const payload = {
    correctCatalogDefaults: [
      {
        asin: asin?.asin,
        obfuscatedMarketplaceId: asin?.obfuscatedMarketplaceId,
        catalogDefaultsValues: {
          brand: brand,
          brandCode: brandCode,
          deprecatedCanBeGiftmessaged: deprecatedCanBeGiftmessaged,
          deprecatedCanBeGiftwrapped: deprecatedCanBeGiftwrapped,
          displayAttribute: displayAttribute,
          displayOnWebsite: displayOnWebsite,
          glProductTypeGroupType: glProductTypeGroupType,
          itemTypeKeyword: itemTypeKeyword,
          manufacturer: manufacturer,
          productCategory: productCategory,
          productSubCategory: productSubCategory,
          productType: productType,
          recommendedBrowseNodes: recommendedBrowseNodes,
          sellerOfRecordVendorId: sellerOfRecordVendorId,
          title: title,
          bulletPoint: bulletPoint,
        },
      },
    ],
  };

  const { data } = await axios.put<PublishCorrectCatalogDefaultsOutput>(
    url,
    payload
  );
  return data;
}
